import "./CreateForm.css";

import { Breadcrumb, Button, Form, Input, message, Select } from "antd";
import { useEffect, useState } from "react";
import { BsTrash } from "react-icons/bs";
import { CgScreen } from "react-icons/cg";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  type CategoryType,
  createForm,
  type CreateFormPageProps,
  deleteForm,
  type FormSubmitType,
  type FormType,
  getCategories,
  getPackages,
  getSubcategories,
  type SubCategoryType,
  updateForm,
  useAppSelector,
} from "../../../_lib/_store";
import { FormBuilder } from "./CreateForm.FormBuilder";

export const CreateFormPage = ({ type }: CreateFormPageProps) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { isLoading } = useAppSelector((state) => state.form);
  const { isLoading: packageLoading, packages } = useAppSelector(
    (state) => state.package,
  );
  const { isLoading: categoryLoading, categories } = useAppSelector(
    (state) => state.category,
  );
  const { isLoading: subcategoryLoading, subCategories } = useAppSelector(
    (state) => state.subCategory,
  );

  const [filteredCategories, setFilteredCategories] = useState<CategoryType[]>(
    [],
  );
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    SubCategoryType[]
  >([]);
  const navigate = useNavigate();
  const { state } = useLocation();

  const isEdit = type === "edit";
  const formObj = state?.form;

  useEffect(() => {
    if (isEdit && !formObj) {
      navigate("/form");
    }
    if (isEdit) {
      form.setFieldsValue(formObj);
      if (formObj?.packageId) {
        setFilteredCategories(
          categories.filter((c) => c.packageId === formObj?.packageId),
        );
      }
      if (formObj?.categoryId) {
        setFilteredSubCategories(
          subCategories.filter((s) => s.categoryId === formObj.categoryId),
        );
      }
    }
  }, [state, packages, categories, subCategories]);

  useEffect(() => {
    getData();
  }, [state]);

  const getData = async () => {
    await getPackages(messageApi);
    await getCategories(messageApi);
    await getSubcategories(messageApi);
  };

  const handleValuesChange = (changedValues: FormType) => {
    if (changedValues.packageId) {
      setFilteredCategories(
        categories.filter((c) => c.packageId === changedValues.packageId),
      );
      form.setFieldsValue({
        categoryId: undefined,
        subCategoryId: undefined,
      });
      setFilteredSubCategories([]);
    }

    if (changedValues.categoryId) {
      setFilteredSubCategories(
        subCategories.filter((s) => s.categoryId === changedValues.categoryId),
      );
      form.setFieldsValue({ subCategoryId: undefined });
    }
    if (changedValues.subCategoryId) {
      setFilteredSubCategories(subCategories);
    }
  };

  const handleSubmit = async (values: FormSubmitType) => {
    const payload = {
      ...values,
      ...(isEdit ? { id: state.form.id } : {}),
    };

    if (values.version) {
      payload.version = Number(values.version);
    } else {
      delete payload.version;
    }

    console.log("🚀 ~ handleSubmit ~ payload:", payload);

    if (isEdit) {
      const res = await updateForm(messageApi, payload);
      if (res) {
        navigate("/form/edit", {
          state: { form: res },
          replace: true,
        });
      }
    } else {
      const res = await createForm(messageApi, payload);
      if (res) {
        form.resetFields();
      }
    }
  };

  const handleDelete = async () => {
    const res = await deleteForm(messageApi, state.form.id);
    if (res) {
      navigate("/form");
    }
  };

  return (
    <>
      {contextHolder}
      <div className="createForm">
        <Breadcrumb
          items={[
            {
              title: <Link to="/dashboard">Dashboard</Link>,
            },
            {
              title: <Link to="/form">Form</Link>,
            },
            {
              title: isEdit ? "Edit" : "Create new",
            },
          ]}
        />

        {!isEdit ? (
          <h2 className="title">Create new</h2>
        ) : (
          <div className="titleHeader">
            <h2 className="title">Edit</h2>
            <div className="actionButton">
              <Button
                icon={<CgScreen size={17} />}
                size="large"
                onClick={() =>
                  navigate(`/form/show`, {
                    state: { form: state?.form },
                  })
                }
              >
                Show
              </Button>
              <Button
                icon={<BsTrash />}
                size="large"
                onClick={handleDelete}
                loading={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="form">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleValuesChange}
          >
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Form title is required" }]}
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Package"
              name="packageId"
              rules={[{ required: true, message: "Package is Required" }]}
              help={
                form.getFieldValue("packageId") ? "" : "This field is required."
              }
            >
              <Select
                loading={packageLoading}
                size="large"
                options={packages?.map((v) => ({
                  label: v?.name,
                  value: v?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Category"
              name="categoryId"
              rules={[{ required: true, message: "Category is Required" }]}
              help={
                form.getFieldValue("categoryId")
                  ? ""
                  : "Please select a package first to enable category selection and this field is required."
              }
            >
              <Select
                loading={categoryLoading}
                size="large"
                options={filteredCategories.map((c) => ({
                  label: c?.name,
                  value: c?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Sub Category"
              name="subCategoryId"
              help={
                form.getFieldValue("subCategoryId")
                  ? ""
                  : "Please select a category first to enable subcategory selection."
              }
            >
              <Select
                loading={subcategoryLoading}
                size="large"
                options={filteredSubCategories.map((s) => ({
                  label: s?.name,
                  value: s?.id,
                }))}
              />
            </Form.Item>

            <Form.Item label="Status" name="status">
              <Select
                loading={subcategoryLoading}
                size="large"
                options={["draft", "published", "archived", "submitted"].map(
                  (v) => ({
                    label: v,
                    value: v,
                  }),
                )}
              />
            </Form.Item>

            <Form.Item label="Version" name="version">
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Form Body"
              name="formBody"
              trigger="onChange"
              valuePropName="value"
            >
              <FormBuilder key={formObj?.id || "new"} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ width: "150px" }}
                loading={isLoading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};
